// =============================================================================
// features/admin/document-types/api.ts — Cliente HTTP do catálogo de tipos de
// documento (F46-S12). Consome as rotas administrativas entregues em F46-S02.
//
// Endpoints:
//   GET   /api/admin/document-types      — lista (inclui inativos por padrão)
//   POST  /api/admin/document-types      — cria
//   PATCH /api/admin/document-types/:id  — edita (key é imutável)
//
// Sem DELETE — tipo em uso vira `active: false` via PATCH (nunca some do
// histórico de documentos já classificados com aquele tipo).
//
// Todas as respostas são validadas via Zod (.parse) antes de retornar —
// padrão M1 do projeto (falha ruidosa se o backend retornar shape inesperado).
// Schemas vêm de @elemento/shared-schemas (F46-S02) — nunca redigitados aqui.
//
// RBAC: documents:manage (admin-only). O backend é a fonte de verdade — este
// arquivo só faz a chamada; o gate de UI vive em DocumentTypesPage.tsx.
//
// LGPD: document_types não contém PII — é metadado de configuração.
// =============================================================================

import type {
  DocumentType,
  DocumentTypeCreate,
  DocumentTypeUpdate,
} from '@elemento/shared-schemas';
import { DocumentTypeListResponseSchema, DocumentTypeSchema } from '@elemento/shared-schemas';

import { api } from '../../../lib/api';

/**
 * GET /api/admin/document-types
 * Lista o catálogo completo da organização (ativos + inativos) — quem
 * administra precisa ver o que desativou.
 */
export async function listDocumentTypes(): Promise<DocumentType[]> {
  const raw = await api.get(`/api/admin/document-types?include_inactive=true`);
  return DocumentTypeListResponseSchema.parse(raw).data;
}

/**
 * POST /api/admin/document-types
 * Cria um novo tipo no catálogo (key imutável após criação).
 */
export async function createDocumentType(body: DocumentTypeCreate): Promise<DocumentType> {
  const raw = await api.post(`/api/admin/document-types`, body);
  return DocumentTypeSchema.parse(raw);
}

/**
 * PATCH /api/admin/document-types/:id
 * Atualiza parcialmente um tipo existente. Não há DELETE — desativar um tipo
 * em uso é `PATCH { active: false }`.
 */
export async function updateDocumentType(
  id: string,
  body: DocumentTypeUpdate,
): Promise<DocumentType> {
  const raw = await api.patch(`/api/admin/document-types/${encodeURIComponent(id)}`, body);
  return DocumentTypeSchema.parse(raw);
}
